import axios, { AxiosResponse } from 'axios';
import delay from 'delay';
import {
    LOGOUT_PATH,
    ME_PATH,
    OTP_CLIENT_SIGN_IN_PATH,
    OTP_SIGN_IN_PATH,
    PASSWORD_RESET_LINK_PATH,
    PASSWORD_RESET_PATH,
    PRIVATE_ROOM_LINK_PATH,
    REFRESH_TOKEN_PATH,
    SIGN_IN_CLIENT_PATH,
    SIGN_IN_PATH,
    SIGN_IN_VERIFY_PATH,
    SIGN_UP_PATH,
    SIGN_UP_V2_PATH,
    SIGN_UP_WITH_GOOGLE_V2_PATH,
    SSO_DOMAIN_SIGN_IN_PATH,
    SSO_SIGN_IN_PATH,
    THERAPIST_REGISTRATION_PATH,
    UPHEAL_API_URL,
    VERIFY_AUTH_PATH,
    VERIFY_CONFIRM_AUTH_PATH,
} from '../configuration';
import {
    AuthResponse,
    Consent,
    Credentials,
    InviteInfo,
    SignUpDetails,
    SSOAuthResponse,
    SSOCredentials,
    VerificationCredentials,
} from '../../types';
import {
    inviteLinkConsentsRequest,
    signInClientRequest,
    signUpRequest,
    validateEmailCodeRequest,
    validateInviteRequest,
} from './../requests';
import { browserStorage } from '../../utils/cache/browserStorage';
import { generate as generateShortUuid } from 'short-uuid';
import logger, {
    sendAxiosRequestAsLog,
    sendAxiosResponseAsLog,
} from '@upheal/utils/logger';
import { BROWSER_SESSION_ID } from '@upheal/utils/browserSession';
import { captureException } from '../sentry';
import { FF_BACKEND_ENV } from '@upheal/utils/feature-flags';

declare module 'axios' {
    export interface AxiosRequestConfig {
        metadata?: { startTime?: number };
        _retry?: boolean;
    }
}

// Set config defaults when creating the instance
export const appServer = axios.create({
    baseURL: UPHEAL_API_URL,
});

const UNAUTHENTICATED_PATHS = [
    THERAPIST_REGISTRATION_PATH,
    PASSWORD_RESET_PATH,
    PASSWORD_RESET_LINK_PATH,
    VERIFY_AUTH_PATH,
    VERIFY_CONFIRM_AUTH_PATH,
    '/auth/sso/authorize',
    '/auth/sso/authorize/google/signup',
    '/auth/detect-provider',
    '/auth/sso/saml/init',
];
const AUTHENTICATION_PATHS = [
    SIGN_IN_PATH,
    SIGN_UP_PATH,
    SIGN_UP_V2_PATH,
    SIGN_UP_WITH_GOOGLE_V2_PATH,
    SIGN_IN_CLIENT_PATH,
    OTP_SIGN_IN_PATH,
    OTP_CLIENT_SIGN_IN_PATH,
    SSO_SIGN_IN_PATH,
    REFRESH_TOKEN_PATH,
    PRIVATE_ROOM_LINK_PATH,
    SIGN_IN_VERIFY_PATH,
    '/video-calls/v1/authorize-contact',
];

export const apiServerController = {
    onRefreshTokenFailure: undefined as (() => void) | undefined,
};

// We want single refresh token request when multiple parallel requests fail with 401.
// And we don't wanna send new requests while refresh token request is in progress.
// Web Locks API ensures only one tab refreshes at a time across all browser tabs.
let refreshAccessTokenPromise: Promise<string> | undefined;

const doRefreshAccessToken = async (): Promise<string> => {
    const currentToken = browserStorage.loadAuthToken();

    const res = await appServer.post(REFRESH_TOKEN_PATH, {
        rememberMe: currentToken?.rememberMe || false,
    });

    if (res.status === 200) {
        const newAuthToken = {
            ...currentToken,
            ...(res.data as AuthResponse),
        };
        browserStorage.saveAuthToken(newAuthToken);
        browserStorage.setLastSignedIn();
        return newAuthToken.accessToken;
    }
    throw new Error(
        'Failed to refresh access token: unexpected status code ' + res.status,
    );
};

const refreshWithWebLocks = async (): Promise<string> => {
    const isWebLocksSupported =
        typeof navigator !== 'undefined' &&
        'locks' in navigator &&
        typeof navigator.locks.request === 'function';
    if (!isWebLocksSupported) {
        // Web Locks API not available, fallback to direct refresh
        logger.warn(
            '[auth] Web Locks API not available, falling back to direct token refresh',
        );
        return doRefreshAccessToken();
    }

    // Capture token BEFORE acquiring lock to detect if another caller refreshes while we wait
    const tokenBeforeLock = browserStorage.loadAuthToken();

    return await navigator.locks.request('upheal-token-refresh', async () => {
        const currentToken = browserStorage.loadAuthToken();

        // If token changed while waiting for lock, another caller already refreshed.
        // Use the new token instead of making another refresh API call.
        if (
            currentToken &&
            tokenBeforeLock?.accessToken !== currentToken.accessToken
        ) {
            logger.debug('[auth] token was refreshed while waiting for lock');
            return currentToken.accessToken;
        }

        logger.debug('[auth] refreshing access token with Web Locks API');
        return await doRefreshAccessToken();
    });
};

export const refreshAccessToken = (): Promise<string> => {
    if (!refreshAccessTokenPromise) {
        refreshAccessTokenPromise = refreshWithWebLocks()
            .catch((error) => {
                if (error?.response?.status >= 400) {
                    browserStorage.clearAuthToken();
                    apiServerController.onRefreshTokenFailure?.();
                    logger.debug('[auth] failed to refresh token', { error });
                }
                throw error;
            })
            .finally(() => {
                refreshAccessTokenPromise = undefined;
            });
    } else {
        logger.debug('[auth] refresh already in progress, waiting for result');
    }

    return refreshAccessTokenPromise;
};

// We want to make sure the access token is always valid during whole request lifecycle. Therefore we need to refresh it if it's close to expiration.
export const tryToEnsureFreshAccessToken = async (): Promise<string | null> => {
    // defer all requests if refresh token request is currently in progress
    if (refreshAccessTokenPromise) {
        await refreshAccessTokenPromise;
    }

    const authToken = browserStorage.loadAuthToken();

    if (!authToken) {
        // if authToken is not present let's skip auth header and try if the request will be successful
        // if not we will try to refresh token (see below - response.interceptor.use(...))
        return null;
    }

    if (
        !authToken.accessTokenExpiresAt ||
        authToken.accessTokenExpiresAt < Date.now()
    ) {
        logger.debug(
            '[auth] tryToEnsureFreshAccessToken: access token expired, refreshing',
        );
        return await refreshAccessToken();
    }

    return authToken.accessToken;
};

// Request interceptors are executed in the reverse order they are defined.
// To have access to correlation-id for logging, this have to be defined first.
appServer.interceptors.request.use((config) => {
    sendAxiosRequestAsLog(config);

    return config;
});

// Response interceptors are executed in the order they are defined.
appServer.interceptors.response.use(
    (response) => {
        sendAxiosResponseAsLog(response.config, response);

        return response;
    },
    (error) => {
        sendAxiosResponseAsLog(error.config, error.response, error);

        return Promise.reject(error);
    },
);

// set start time for each request
appServer.interceptors.request.use((config) => {
    config.metadata = { startTime: performance.now() };
    return config;
});

//request interceptor to add the auth token header to requests
appServer.interceptors.request.use(
    async (config) => {
        config.headers = config.headers || {};
        const correlationIds = [`web-${generateShortUuid()}`];

        if (FF_BACKEND_ENV.value) {
            if (/^[a-zA-Z-]+$/.test(FF_BACKEND_ENV.value)) {
                correlationIds.push(`feature:${FF_BACKEND_ENV.value}`);
            } else {
                logger.warn('[api] Invalid FF_BACKEND_ENV value, skipping', {
                    value: FF_BACKEND_ENV.value,
                });
            }
        }

        config.headers['correlation-id'] = correlationIds.join(',');
        config.headers['frontend-session-id'] = BROWSER_SESSION_ID;
        if (config.method?.toUpperCase() !== 'GET') {
            config.headers['X-Nonce'] ||= generateShortUuid();
        }

        const isAuthenticationPath =
            config.url && AUTHENTICATION_PATHS.includes(config.url);
        if (isAuthenticationPath) {
            // As per https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials
            // we need to set this flag to allow backend setting refresh token
            // cookie in the response:
            // "In addition, this flag is also used to indicate when cookies
            // are to be ignored in the response.The default is false.
            // TODO: why is this not needed for refresh token call? Is this needed at all?
            config.withCredentials = true;
        }

        const isUnauthenticatedPath =
            config.url && UNAUTHENTICATED_PATHS.includes(config.url);

        if (isAuthenticationPath || isUnauthenticatedPath) {
            // don't add access token for refresh token call, auth calls or unauthed calls
            return config;
        }

        const accessToken = await tryToEnsureFreshAccessToken();
        config.headers!['Authorization'] = accessToken
            ? `Bearer ${accessToken}`
            : '';

        return config;
    },
    (error) => {
        Promise.reject(error);
    },
);

//response interceptor to refresh token on receiving token expired error
appServer.interceptors.response.use(
    (response) => {
        return response;
    },
    async function (error) {
        const originalRequest = error.config;

        if (
            error.response?.status === 401 &&
            originalRequest.url !== REFRESH_TOKEN_PATH &&
            !UNAUTHENTICATED_PATHS.includes(originalRequest.url) &&
            !originalRequest.withCredentials &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;

            try {
                await refreshAccessToken();
            } catch {
                return Promise.reject(error);
            }

            return appServer(originalRequest);
        }

        return Promise.reject(error);
    },
);

export const logUnexpectedError = (props: {
    errorTitle: string;
    errorCode?: number;
    correlationId?: string;
    requestHeaders?: Record<string, string>;
    responseHeaders?: Record<string, string>;
    extra?: Record<string, unknown>;
}): Error => {
    const {
        errorTitle,
        errorCode = 500,
        correlationId = 'unknown',
        requestHeaders,
        responseHeaders,
        extra,
    } = props;

    // Historically we only logged 500 reponses this way, but not we also log all 5XX and unexpected errors from GraphQL (which doesn't use HTTP status codes at all)
    const loggedError = new Error(`${errorCode}: ${errorTitle}`);
    loggedError.name = 'Response500Error';

    captureException(loggedError, extra, {
        contexts: {
            requestHeaders,
            responseHeaders,
        },
        extra: {
            correlationId,
        },
        tags: {
            correlationId,
        },
    });

    return loggedError;
};

export const shouldRetryRequestWithoutResponse = ({
    alreadyRetried,
    isIdempotent,
    requestDuration,
}: {
    alreadyRetried: boolean;
    isIdempotent: boolean;
    requestDuration: number;
}): boolean => {
    if (alreadyRetried) {
        return false;
    }

    // we can retry idempotent (GET requests / GraphQL Queries) regardless if they reached the server or not
    if (isIdempotent) {
        return true;
    }

    // this should indicate the request never reached the server,
    // but failed on eg.: firewall, DNS, API gateway, CloudFront, etc.
    const isShortRequest = requestDuration < 500;

    return isShortRequest;
};

export const logRequestWithoutResponse = (props: {
    errorTitle: string;
    correlationId?: string;
    requestHeaders?: Record<string, string>;
    extra?: Record<string, unknown>;
}): Error => {
    const {
        errorTitle,
        correlationId = 'unknown',
        requestHeaders,
        extra,
    } = props;

    const loggedError = new Error(
        `NoResponse: ${errorTitle} [${correlationId}]`,
    );
    loggedError.name = 'RequestWithoutResponseError';

    captureException(loggedError, extra, {
        contexts: {
            requestHeaders,
        },
    });

    return loggedError;
};

appServer.interceptors.response.use(
    (response) => response,
    async function (error) {
        const originalRequest = error.config;
        const correlationId = originalRequest.headers?.['correlation-id'];
        const requestMethod = originalRequest.method.toUpperCase();
        const requestUrl = originalRequest.url;
        const requestDuration =
            performance.now() - originalRequest.metadata?.startTime;

        if (!error.response || error.response.status === 0) {
            if (
                shouldRetryRequestWithoutResponse({
                    alreadyRetried: originalRequest._retry,
                    isIdempotent: requestMethod === 'GET',
                    requestDuration,
                })
            ) {
                originalRequest._retry = true;

                try {
                    return await appServer(originalRequest);
                } catch {
                    // just proceed and return original error
                }
            } else {
                logRequestWithoutResponse({
                    errorTitle: `${requestMethod} ${requestUrl}`,
                    correlationId,
                    requestHeaders: originalRequest.headers,
                    extra: {
                        error,
                        response: error.response,
                        requestDuration,
                    },
                });
            }
        }

        if (error.response?.status && error.response.status >= 500) {
            logUnexpectedError({
                errorTitle: `${requestMethod} ${requestUrl}`,
                errorCode: error.response.status,
                correlationId,
                requestHeaders: originalRequest.headers,
                responseHeaders: error.response.headers,
                extra: {
                    error,
                    requestDuration,
                },
            });
        }

        // Enhance logs
        Object.assign(error, {
            correlationId,
            requestMethod,
            requestUrl,
            requestDuration,
            errorCode: error.response?.status,
        });
        return Promise.reject(error);
    },
);

export const retryIfFailed = <T extends Promise<AxiosResponse>>(
    originalRequestPromise: T,
    options: {
        minTimeout?: number;
        factor?: number; // exponential factor of minTimeout
        retries?: number;
    } = {},
): { original: T; retried: T; abort: () => void } => {
    const { minTimeout = 1000, retries = 5, factor = 2 } = options;
    let retriesCounter = 0;
    const abortController = new AbortController();

    const abort = () => {
        abortController.abort();
    };
    const retry = (promise: T): T =>
        promise.catch(async (error) => {
            try {
                await delay(minTimeout * factor ** retriesCounter, {
                    signal: abortController.signal,
                });
            } catch {
                return Promise.reject('Retry Cancelled');
            }

            const originalRequest = error.config;
            retriesCounter++;

            return retriesCounter >= retries
                ? appServer.request(originalRequest)
                : retry(appServer.request(originalRequest) as T);
        }) as T;

    const retriedPromise = retry(originalRequestPromise);

    return {
        original: originalRequestPromise,
        retried: retriedPromise,
        abort,
    };
};

const api = {
    signIn: async (body: Credentials) =>
        appServer
            .post(SIGN_IN_PATH, body)
            .then((response) => {
                const authResponse = response.data as AuthResponse;
                if (authResponse.verificationId) {
                    return {
                        verificationRequired: true,
                        verificationId: authResponse.verificationId,
                        redactedPhone: authResponse.redactedPhone,
                    };
                }
                browserStorage.saveAuthToken({
                    ...authResponse,
                    rememberMe: body.rememberMe,
                });
                return { verificationRequired: false };
            })
            .catch((error) => {
                if (error.response?.status !== 401) {
                    captureException('Failed to sign in.', {
                        error,
                    });
                }

                throw error;
            }),

    signInVerify: async (body: VerificationCredentials) =>
        appServer
            .post(SIGN_IN_VERIFY_PATH, body)
            .then((response) => {
                const { verificationId, redactedPhone, ...authResponse } =
                    response.data;
                browserStorage.saveAuthToken({
                    ...authResponse,
                    rememberMe: body.rememberMe,
                });
            })
            .catch((error) => {
                captureException(
                    'Failed to verify sign in.',
                    {
                        error,
                    },
                    {
                        level: 'warning',
                    },
                );

                throw error;
            }),

    signInWithOtp: async (otp: string) =>
        appServer
            .post(OTP_SIGN_IN_PATH, { otp })
            .then((response) => {
                const authResponse = response.data as AuthResponse;
                browserStorage.saveAuthToken({ ...authResponse, otp });
            })
            .catch((error) => {
                captureException('Failed to sign in using OTP.', {
                    error,
                });
                throw error;
            }),

    singInWithSSO: async (
        body: SSOCredentials,
        enableForDomain: boolean = false,
    ) =>
        appServer
            .post(
                enableForDomain ? SSO_DOMAIN_SIGN_IN_PATH : SSO_SIGN_IN_PATH,
                body,
            )
            .then((response) => {
                const authResponse = response.data as SSOAuthResponse;
                browserStorage.saveAuthToken({
                    ...authResponse,
                    // TBD: even though expiresInSeconds is deprecated we are still using it
                    // and without it the refresh token check doesn't work
                    expiresInSeconds: authResponse.accessTokenExpiresInSeconds,
                    rememberMe: body.rememberMe,
                });
            })
            .catch((error) => {
                captureException('Failed to sign in using SSO.', {
                    error,
                });
                throw error;
            }),

    signInClient: async (inviteId: string) =>
        appServer
            .request(signInClientRequest(inviteId))
            .then(({ data }) => {
                const authResponse: AuthResponse = {
                    accessToken: data.accessToken,
                    expiresInSeconds: data.expiresInSeconds,
                    accessTokenExpiresInSeconds:
                        data.accessTokenExpiresInSeconds,
                    refreshTokenExpiresInSeconds:
                        data.refreshTokenExpiresInSeconds,
                };
                browserStorage.saveAuthToken({
                    ...authResponse,
                    isClient: true,
                });

                const missingConsents: Array<Consent> | null =
                    data.requiredConsents;
                const ownerUserId: string = data.ownerUserId;
                const organizationId: string | null = data.organizationId;
                const organizationName: string | null = data.organizationName;
                const organization =
                    organizationId && organizationName
                        ? {
                              id: organizationId,
                              name: organizationName,
                          }
                        : null;

                return { missingConsents, ownerUserId, organization };
            })
            .catch((error) => {
                captureException('Failed to sign in client.', {
                    error,
                });
                throw error;
            }),

    validateInvite: async (inviteId: string) =>
        appServer.request(validateInviteRequest(inviteId)).then((response) => {
            const inviteInfo: InviteInfo = response.data;
            return inviteInfo;
        }),

    validateEmailCode: async (code: string) =>
        appServer.request(validateEmailCodeRequest(code)).then((response) => {
            const inviteInfo: InviteInfo = response.data;
            return inviteInfo;
        }),

    getInviteConsents: async (inviteId: string) =>
        appServer
            .request(inviteLinkConsentsRequest(inviteId))
            .then((response) => {
                const consents: Array<Consent> = response.data;
                return consents;
            }),

    signUp: async (details: SignUpDetails) =>
        appServer
            .request(signUpRequest(details))
            .then((response) => {
                const authResponse = response.data as AuthResponse;
                browserStorage.saveAuthToken(authResponse);
            })
            .catch((error) => {
                captureException('Failed to sign up.', {
                    error,
                });
                throw error;
            }),

    logout: () => {
        return appServer.post(LOGOUT_PATH);
    },

    getMe: (): Promise<AxiosResponse> => {
        try {
            return appServer.get(ME_PATH);
        } catch (error) {
            captureException('Failed to fetch /me.', {
                error,
            });
            throw error;
        }
    },
};

export default api;
